<template>
  <main class="login-shell">
    <section class="login-side">
      <header class="brand" aria-label="MediaLink">
        <svg class="brand-mark" viewBox="0 0 52 52" fill="none" aria-hidden="true">
          <rect x="1" y="1" width="50" height="50" rx="16" fill="#F2F7FF" />
          <path d="M8 28.5C13.5 28.5 14.5 18 20 18C25.5 18 26 34 32 34C38 34 39 23.5 45 23.5" stroke="#1769FF" stroke-width="4.2" stroke-linecap="round" />
          <path d="M8 35.5C13.5 35.5 15 30 20 30C25 30 27 39 32 39C37 39 40 33 45 33" stroke="#65A2FF" stroke-width="2.5" stroke-linecap="round" />
        </svg>
        <span>Media<span class="brand-light">Link</span></span>
      </header>

      <div class="form-wrap">
        <div class="eyebrow"><span class="status-dot"></span> ESPACE ADMINISTRATEUR</div>
        <template v-if="!resettingPassword">
          <h1>Ravi de vous revoir.</h1>
          <p class="intro">Connectez-vous à votre espace pour piloter vos équipements et vos services en toute simplicité.</p>
        </template>
        <template v-else>
          <h1>{{ resetStep === 1 ? 'Réinitialiser le mot de passe' : 'Choisissez un nouveau mot de passe' }}</h1>
          <p class="intro">{{ resetStep === 1 ? 'Saisissez votre adresse professionnelle pour commencer.' : 'Créez un mot de passe sécurisé pour votre compte.' }}</p>
        </template>

        <form class="login-form" @submit.prevent="handleSubmit">
          <div v-if="!resettingPassword || resetStep === 1" class="field">
            <label for="email">Adresse e-mail professionnelle</label>
            <input id="email" v-model.trim="email" type="email" autocomplete="username" placeholder="nom@entreprise.fr" required />
          </div>

          <template v-if="!resettingPassword">
            <div class="field">
              <div class="label-row">
                <label for="password">Mot de passe</label>
                <button type="button" class="text-button" @click="startReset">Mot de passe oublié ?</button>
              </div>
              <input id="password" v-model="password" type="password" autocomplete="current-password" placeholder="Saisissez votre mot de passe" required />
            </div>
            <label class="remember-row">
              <input v-model="remember" type="checkbox" />
              <span>Rester connecté</span>
            </label>
          </template>

          <template v-else-if="resetStep === 2">
            <div class="field">
              <label for="new-password">Nouveau mot de passe</label>
              <input id="new-password" v-model="newPassword" type="password" autocomplete="new-password" placeholder="8 caractères minimum" required />
              <p class="field-hint">Au moins 8 caractères, une majuscule, un chiffre et un caractère spécial.</p>
            </div>
            <div class="field">
              <label for="confirm-password">Confirmer le mot de passe</label>
              <input id="confirm-password" v-model="confirmPassword" type="password" autocomplete="new-password" placeholder="Ressaisissez le mot de passe" required />
            </div>
            <p v-if="passwordError" class="error-message" role="alert">{{ passwordError }}</p>
          </template>

          <button class="submit-button" type="submit" :disabled="loading">
            <span>{{ loading ? 'Connexion en cours…' : buttonLabel }}</span>
            <svg v-if="!loading" viewBox="0 0 20 20" fill="none" aria-hidden="true"><path d="M4 10h11M10 5l5 5-5 5" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" /></svg>
          </button>
          <button v-if="resettingPassword" type="button" class="back-button" @click="cancelReset">Retour à la connexion</button>
        </form>

        <div class="secure-note"><svg viewBox="0 0 20 20" fill="none" aria-hidden="true"><path d="M10 2.5 16 5v4.6c0 3.8-2.5 6.3-6 7.9-3.5-1.6-6-4.1-6-7.9V5l6-2.5Z" stroke="currentColor" stroke-width="1.5" stroke-linejoin="round"/><path d="m7.4 9.8 1.7 1.7 3.6-3.7" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg> Connexion sécurisée à votre espace MediaLink</div>
      </div>

      <footer class="login-footer">
        <span>© {{ new Date().getFullYear() }} MediaLink</span>
        <a href="mailto:support@medialink.fr">Besoin d’aide ?</a>
      </footer>
    </section>

    <aside class="visual-side" aria-label="Aperçu de la plateforme MediaLink">
      <div class="ambient ambient-one"></div>
      <div class="ambient ambient-two"></div>
      <div class="visual-topline"><span class="live-indicator"></span> VOTRE ENVIRONNEMENT, EN UN SEUL ENDROIT</div>

      <div class="visual-copy">
        <div class="wave-emblem" aria-hidden="true">
          <svg viewBox="0 0 120 64" fill="none"><path d="M4 35C19 35 21 10 37 10C53 10 55 53 71 53C87 53 91 23 116 23" stroke="currentColor" stroke-width="6" stroke-linecap="round"/><path d="M4 51C19 51 24 35 37 35C50 35 57 61 72 61C87 61 95 43 116 43" stroke="currentColor" stroke-width="3" stroke-linecap="round" opacity=".45"/></svg>
        </div>
        <h2>Tout votre réseau.<br /><span>Un seul espace.</span></h2>
        <p>Supervisez vos écrans, bornes et flux en temps réel grâce à une plateforme conçue pour simplifier vos opérations.</p>
      </div>

      <div class="dashboard-card">
        <div class="dashboard-header">
          <div><span class="mini-label">VUE D’ENSEMBLE</span><h3>État de votre parc</h3></div>
          <span class="online-pill"><span></span> En ligne</span>
        </div>
        <div class="metric-grid">
          <div class="metric"><span class="metric-icon screen-icon">▤</span><span class="metric-label">Écrans actifs</span><strong>24 <small>/ 24</small></strong><div class="metric-track"><span style="width:100%"></span></div></div>
          <div class="metric"><span class="metric-icon kiosk-icon">▦</span><span class="metric-label">Bornes connectées</span><strong>12 <small>/ 12</small></strong><div class="metric-track"><span style="width:100%"></span></div></div>
        </div>
        <div class="activity-row"><div class="activity-symbol">↗</div><div class="activity-text"><strong>Synchronisation en temps réel</strong><span>Les équipements sont à jour</span></div><span class="activity-check">✓</span></div>
        <div class="dashboard-bottom"><span><i></i> Services opérationnels</span><span>Mis à jour à l’instant</span></div>
      </div>

      <div class="visual-footer"><span>Simple à piloter.</span><span>Fiable au quotidien.</span><span class="footer-wave">M / L</span></div>
    </aside>
  </main>
</template>

<script>
import { toast } from "../main";

export default {
  name: "MediaLinkLoginPage",
  data() {
    return {
      email: "",
      password: "",
      remember: false,
      resettingPassword: false,
      resetStep: 1,
      newPassword: "",
      confirmPassword: "",
      passwordError: "",
      loading: false
    };
  },
  computed: {
    buttonLabel() {
      if (!this.resettingPassword) return "Se connecter";
      return this.resetStep === 1 ? "Continuer" : "Réinitialiser le mot de passe";
    }
  },
  methods: {
    async login() {
      this.loading = true;
      try {
        const response = await fetch("https://api.datafuse.fr/api/super-admin/login", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ email: this.email, password: this.password })
        });
        const data = await response.json();
        if (!response.ok) {
          toast.error(data.error || "Impossible de vous connecter. Vérifiez vos identifiants.");
          return;
        }
        localStorage.setItem("superAdminToken", data.token);
        if (this.remember) localStorage.setItem("mediaLinkRememberedEmail", this.email);
        else localStorage.removeItem("mediaLinkRememberedEmail");
        toast.success("Connexion réussie.");
        this.$router.push("/");
      } catch (error) {
        console.error("Erreur de connexion MediaLink :", error);
        toast.error("Le serveur est momentanément inaccessible. Réessayez dans un instant.");
      } finally {
        this.loading = false;
      }
    },
    startReset() {
      this.resettingPassword = true;
      this.resetStep = 1;
      this.passwordError = "";
    },
    cancelReset() {
      this.resettingPassword = false;
      this.resetStep = 1;
      this.newPassword = "";
      this.confirmPassword = "";
      this.passwordError = "";
    },
    handleSubmit() {
      if (!this.resettingPassword) {
        this.login();
        return;
      }
      // Le flux de réinitialisation doit être relié à un endpoint backend avant mise en production.
      if (this.resetStep === 1) {
        toast.info("La réinitialisation doit être activée par le support MediaLink.");
        return;
      }
      const regex = /^(?=.*[A-Z])(?=.*\d)(?=.*[!@#$%^&*()_\-+=<>?]).{8,}$/;
      if (!regex.test(this.newPassword)) {
        this.passwordError = "Utilisez au moins 8 caractères, une majuscule, un chiffre et un caractère spécial.";
        return;
      }
      if (this.newPassword !== this.confirmPassword) {
        this.passwordError = "Les deux mots de passe ne correspondent pas.";
        return;
      }
      toast.info("La réinitialisation doit être reliée au serveur avant de pouvoir modifier votre mot de passe.");
    }
  },
  mounted() {
    const rememberedEmail = localStorage.getItem("mediaLinkRememberedEmail");
    if (rememberedEmail) {
      this.email = rememberedEmail;
      this.remember = true;
    }
  }
};
</script>

<style>
@import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700&family=Manrope:wght@400;500;600;700;800&display=swap');

:root { font-family: 'DM Sans', sans-serif; color: #111827; font-synthesis: none; }
* { box-sizing: border-box; }
body { margin: 0; background: #f5f7fb; }
button, input { font: inherit; }
.login-shell { min-height: 100vh; display: grid; grid-template-columns: minmax(420px, 0.92fr) minmax(500px, 1.08fr); background: #fff; }
.login-side { min-height: 100vh; padding: 34px clamp(28px, 5.4vw, 88px) 24px; display: flex; flex-direction: column; }
.brand { display: inline-flex; align-items: center; gap: 12px; width: fit-content; color: #101828; font: 800 23px 'Manrope', sans-serif; letter-spacing: -1.1px; }
.brand-mark { width: 48px; height: 48px; }
.brand-light { color: #1769ff; font-weight: 600; }
.form-wrap { width: 100%; max-width: 420px; margin: auto; padding: 54px 0 60px; }
.eyebrow { display: flex; align-items: center; gap: 9px; color: #64748b; font-size: 10px; font-weight: 800; letter-spacing: 1.8px; margin-bottom: 20px; }
.status-dot { width: 7px; height: 7px; border-radius: 50%; background: #1769ff; box-shadow: 0 0 0 4px #eaf1ff; }
h1 { margin: 0; color: #111827; font: 800 clamp(31px, 3vw, 39px)/1.16 'Manrope', sans-serif; letter-spacing: -1.8px; }
.intro { margin: 15px 0 34px; max-width: 390px; color: #778196; font-size: 14px; line-height: 1.75; }
.login-form { display: flex; flex-direction: column; gap: 22px; }
.field { display: flex; flex-direction: column; gap: 9px; }
.field label, .label-row label { color: #293449; font-size: 12px; font-weight: 700; }
.label-row { display: flex; justify-content: space-between; align-items: center; gap: 12px; }
.field input { width: 100%; height: 52px; border: 1px solid #e1e7f0; border-radius: 12px; outline: none; background: #fbfcfe; color: #111827; padding: 0 15px; font-size: 13px; transition: border-color .18s, box-shadow .18s, background .18s; }
.field input::placeholder { color: #a2abba; }
.field input:focus { border-color: #1769ff; background: #fff; box-shadow: 0 0 0 4px rgba(23,105,255,.09); }
.text-button { padding: 0; border: 0; background: none; color: #1769ff; font-size: 11px; font-weight: 700; cursor: pointer; }
.text-button:hover, .back-button:hover, .login-footer a:hover { text-decoration: underline; }
.remember-row { display: flex; align-items: center; gap: 10px; margin-top: -3px; color: #667085; font-size: 12px; cursor: pointer; }
.remember-row input { width: 15px; height: 15px; margin: 0; accent-color: #1769ff; }
.submit-button { display: flex; align-items: center; justify-content: center; gap: 12px; width: 100%; min-height: 53px; margin-top: 2px; padding: 0 18px; border: 0; border-radius: 12px; background: #1769ff; color: #fff; font-size: 13px; font-weight: 700; cursor: pointer; box-shadow: 0 9px 20px rgba(23,105,255,.18); transition: transform .18s, background .18s, box-shadow .18s; }
.submit-button:hover { background: #0758eb; box-shadow: 0 12px 24px rgba(23,105,255,.25); transform: translateY(-1px); }
.submit-button:disabled { opacity: .7; cursor: wait; transform: none; }
.submit-button svg { width: 18px; height: 18px; }
.back-button { align-self: center; margin-top: -8px; border: 0; background: none; color: #64748b; font-size: 12px; cursor: pointer; }
.field-hint { margin: 0; color: #8993a4; font-size: 11px; line-height: 1.5; }
.error-message { margin: -10px 0 0; color: #d92d20; font-size: 12px; }
.secure-note { display: flex; align-items: center; justify-content: center; gap: 8px; margin-top: 28px; color: #9aa3b2; font-size: 10px; }
.secure-note svg { width: 17px; height: 17px; color: #1769ff; }
.login-footer { display: flex; justify-content: space-between; gap: 15px; color: #a0a8b6; font-size: 11px; }
.login-footer a { color: #667085; text-decoration: none; }
.visual-side { position: relative; min-height: 100vh; overflow: hidden; display: flex; flex-direction: column; justify-content: space-between; padding: clamp(34px, 4vw, 62px); background: #07132b; color: #fff; isolation: isolate; }
.visual-side::before { position: absolute; z-index: -1; inset: 0; content: ''; background: radial-gradient(ellipse at 80% 15%, rgba(23,105,255,.23), transparent 38%), linear-gradient(145deg, #0b1a39 0%, #07132b 55%, #09152d 100%); }
.ambient { position: absolute; z-index: -1; border: 1px solid rgba(97,151,255,.13); border-radius: 50%; pointer-events: none; }
.ambient-one { width: 620px; height: 620px; top: -280px; right: -220px; box-shadow: 0 0 0 50px rgba(97,151,255,.025), 0 0 0 100px rgba(97,151,255,.02); }
.ambient-two { width: 460px; height: 460px; bottom: -300px; left: -200px; box-shadow: 0 0 0 50px rgba(97,151,255,.025); }
.visual-topline { display: flex; align-items: center; gap: 10px; color: #a7b9d8; font-size: 9px; font-weight: 700; letter-spacing: 1.7px; }
.live-indicator { width: 7px; height: 7px; background: #3f8cff; border-radius: 50%; box-shadow: 0 0 12px rgba(63,140,255,.8); }
.visual-copy { max-width: 570px; margin: auto 0 34px; padding-top: 48px; }
.wave-emblem { display: flex; align-items: center; justify-content: center; width: 72px; height: 64px; margin-bottom: 26px; border: 1px solid rgba(105,158,255,.2); border-radius: 18px; background: rgba(36,104,255,.09); color: #438bff; }
.wave-emblem svg { width: 50px; overflow: visible; }
.visual-copy h2 { margin: 0; font: 800 clamp(34px, 4vw, 53px)/1.12 'Manrope', sans-serif; letter-spacing: -2px; }
.visual-copy h2 span { color: #4b91ff; }
.visual-copy p { max-width: 440px; margin: 19px 0 0; color: #a7b5ce; font-size: 14px; line-height: 1.8; }
.dashboard-card { width: 100%; max-width: 580px; margin: 0 auto; padding: 23px; border: 1px solid rgba(159,186,232,.17); border-radius: 19px; background: linear-gradient(145deg, rgba(255,255,255,.09), rgba(255,255,255,.035)); box-shadow: 0 22px 60px rgba(0,0,0,.2); backdrop-filter: blur(16px); }
.dashboard-header { display: flex; justify-content: space-between; align-items: center; gap: 14px; margin-bottom: 20px; }
.mini-label { color: #8fa5c8; font-size: 8px; font-weight: 800; letter-spacing: 1.5px; }
.dashboard-header h3 { margin: 6px 0 0; color: #f7f9ff; font: 700 17px 'Manrope', sans-serif; }
.online-pill { display: inline-flex; align-items: center; gap: 7px; padding: 7px 10px; border: 1px solid rgba(74,222,128,.18); border-radius: 20px; background: rgba(34,197,94,.08); color: #9be8b7; font-size: 9px; font-weight: 700; }
.online-pill span { width: 5px; height: 5px; border-radius: 50%; background: #4ade80; }
.metric-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 12px; }
.metric { position: relative; padding: 15px; border: 1px solid rgba(159,186,232,.12); border-radius: 13px; background: rgba(3,12,29,.22); }
.metric-icon { display: inline-flex; align-items: center; justify-content: center; width: 29px; height: 29px; margin-bottom: 12px; border-radius: 9px; background: rgba(50,125,255,.15); color: #70a7ff; font-size: 17px; }
.kiosk-icon { background: rgba(133,112,255,.15); color: #b1a3ff; }
.metric-label { display: block; color: #9cacc6; font-size: 10px; }
.metric strong { display: block; margin-top: 5px; color: #fff; font: 800 25px 'Manrope', sans-serif; letter-spacing: -.8px; }
.metric strong small { color: #8496b4; font-size: 12px; font-weight: 500; }
.metric-track { height: 4px; margin-top: 12px; overflow: hidden; border-radius: 5px; background: rgba(255,255,255,.09); }
.metric-track span { display: block; height: 100%; border-radius: inherit; background: linear-gradient(90deg, #1769ff, #69a6ff); }
.activity-row { display: flex; align-items: center; gap: 12px; margin-top: 13px; padding: 14px; border: 1px solid rgba(159,186,232,.12); border-radius: 13px; }
.activity-symbol { display: flex; align-items: center; justify-content: center; width: 32px; height: 32px; border-radius: 10px; background: rgba(23,105,255,.15); color: #72a8ff; font-size: 20px; }
.activity-text { display: flex; flex: 1; flex-direction: column; gap: 4px; }
.activity-text strong { color: #e6edfa; font-size: 10px; }
.activity-text span { color: #91a2bf; font-size: 9px; }
.activity-check { display: flex; align-items: center; justify-content: center; width: 20px; height: 20px; border-radius: 50%; background: rgba(34,197,94,.13); color: #7be4a0; font-size: 12px; }
.dashboard-bottom { display: flex; justify-content: space-between; gap: 12px; margin-top: 17px; color: #8093b2; font-size: 9px; }
.dashboard-bottom span:first-child { display: flex; align-items: center; gap: 7px; color: #a4b4cd; }
.dashboard-bottom i { width: 5px; height: 5px; border-radius: 50%; background: #4ade80; }
.visual-footer { display: flex; align-items: center; gap: 9px; margin-top: 30px; color: #8295b6; font-size: 10px; }
.visual-footer span + span:not(.footer-wave)::before { content: '·'; margin-right: 9px; color: #3d76c9; }
.footer-wave { margin-left: auto; color: #6486bb; font: 800 13px 'Manrope', sans-serif; letter-spacing: 1px; }
@media (max-width: 980px) { .login-shell { grid-template-columns: minmax(360px, .9fr) minmax(400px, 1.1fr); } .login-side { padding-right: 36px; padding-left: 36px; } .visual-side { padding: 34px; } .visual-copy h2 { font-size: 38px; } }
@media (max-width: 760px) { .login-shell { display: block; } .login-side { min-height: 100vh; padding: 24px 25px 20px; } .form-wrap { max-width: 440px; padding: 60px 0; } .visual-side { display: none; } .brand-mark { width: 43px; height: 43px; } .brand { font-size: 21px; } h1 { font-size: 34px; } }
@media (prefers-reduced-motion: reduce) { *, *::before, *::after { scroll-behavior: auto !important; transition-duration: .01ms !important; animation-duration: .01ms !important; } }
</style>
